/**
 * mapSlaResponse — pure mapping layer.
 *
 * Turns raw checkDeliveryDetails (v2) SLA options into render-ready view models.
 * Contains NO React and NO rendering concerns so it can be unit-tested in
 * isolation and reused by PDP / Cart on desktop. The presentation components
 * depend only on the SlaOption / SlaViewModel view models produced here.
 */
import {
  DeliveryMethod,
  PriceType,
  PriceTypeTags,
  RawProductServiceability,
  RawSkuEntry,
  RawSlaOption,
  RawSlaResponse,
  SlaOption,
  SlaViewModel
} from './types';

const toNumber = (value?: string): number => {
  const parsed = parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const tagFor = (priceType: PriceType, tags?: PriceTypeTags): string =>
  tags?.[priceType] || priceType.toUpperCase();

/**
 * Map scopedView API priceType → UI / Amplitude priceTypeTags keys.
 * BEST_PRICE → best, BEST_SPEED → fastest (also accepts legacy best/fastest).
 * Used for PDP scopedView; cart deliveryChoices typically already send best/fastest.
 */
const normalizePriceType = (priceType?: string): PriceType => {
  const key = String(priceType || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  if (key === 'BEST_SPEED' || key === 'FASTEST') {
    return 'fastest';
  }
  return 'best';
};

const normalizeDeliveryMethod = (method?: string): DeliveryMethod | undefined => {
  const upper = String(method || '').toUpperCase();
  if (upper === 'RUSH') return 'RUSH';
  if (upper === 'PRIORITY') return 'PRIORITY';
  if (upper === 'STANDARD') return 'STANDARD';
  return undefined;
};

/** Payloads carry either `serviceability` or the misspelled `servicability`. */
const isServiceable = (entry?: { serviceability?: boolean; servicability?: boolean }): boolean =>
  Boolean(entry?.serviceability ?? entry?.servicability);

/**
 * Pick the productDetails entry that belongs to this option.
 *
 * An option can carry several entries (e.g. STANDARD alongside RUSH). Taking
 * the first serviceable one makes a "fastest" option render the STANDARD date
 * and lose the cutoff, so match the entry to the priceType first. Serviceable
 * entries win; a non-serviceable one is still returned so the UI can show why.
 */
const pickProductDetail = (
  details: RawProductServiceability[],
  priceType: PriceType
): RawProductServiceability | undefined => {
  const pool = details.filter(isServiceable);
  const candidates = pool.length ? pool : details;

  const matchesPriceType = (detail: RawProductServiceability): boolean => {
    const method = normalizeDeliveryMethod(detail?.deliveryMethod);
    return priceType === 'fastest'
      ? method === 'RUSH' || method === 'PRIORITY'
      : method === 'STANDARD';
  };

  return candidates.find(matchesPriceType) ?? candidates[0];
};

/**
 * Map a single raw option to a view model. The matched product entry drives
 * delivery + cod; it falls back to the non-serviceable entry so the UI can
 * still show a "not serviceable" reason.
 */
export const mapSlaOption = (raw: RawSlaOption, tags?: PriceTypeTags): SlaOption => {
  const details = raw.serviceabilityDetails?.productDetails ?? [];
  const priceType = normalizePriceType(raw.priceType);
  const product = pickProductDetail(details, priceType);

  const serviceable = isServiceable(raw.serviceabilityDetails) && isServiceable(product);
  const fee = toNumber(product?.deliveryFee);

  const priceData = raw.price?.priceData;
  const wasPriceData = raw.price?.wasPriceData;

  const stockStatus = raw.stock?.stockLevelStatus;

  return {
    priceType,
    // Sent as-is on cart/wishlist requests — never the normalised key.
    rawPriceType: raw.priceType || '',
    tagLabel: tagFor(priceType, tags),
    isSelected: Boolean(raw.isSelected),
    priceGroupId: raw.priceGroupId,
    serviceable,
    reasonForNotServiceability: product?.reasonForNotServiceability,
    codEligible: Boolean(raw.serviceabilityDetails?.codEligible ?? product?.codEligible),
    price: {
      current: priceData?.displayformattedValue || priceData?.formattedValue,
      was: wasPriceData?.displayformattedValue || wasPriceData?.formattedValue,
      discount: priceData?.discountPercent
    },
    delivery: {
      deliveryMethod: normalizeDeliveryMethod(product?.deliveryMethod),
      deliverySLA: product?.deliverySLA,
      eddUpper: product?.eddUpper,
      cutOffTime: product?.cutOffTime,
      fee,
      isFree: fee === 0
    },
    stock: {
      status: stockStatus,
      level: raw.stock?.stockLevel,
      isLowStock: stockStatus === 'lowStock'
    }
  };
};

/**
 * Map a list of raw options (belonging to one sku) into the view model.
 * De-dupes by priceType (keeps first). Order is left exactly as the API sent
 * it — the backend owns the card order, matching the msite mapper.
 */
export const mapSlaOptions = (
  rawOptions: RawSlaOption[] | undefined,
  tags?: PriceTypeTags
): SlaViewModel => {
  const options = (rawOptions ?? [])
    // Keep options the API sent without a priceType — they normalise to "best"
    // rather than disappearing from the list.
    .filter(Boolean)
    .reduce<SlaOption[]>((acc, raw) => {
      if (acc.some(o => o.priceType === normalizePriceType(raw.priceType))) return acc;
      acc.push(mapSlaOption(raw, tags));
      return acc;
    }, []);

  // Sole serviceable card is always the selected option.
  const serviceable = options.filter(option => option.serviceable);
  if (serviceable.length === 1) {
    const onlyId = serviceable[0].priceGroupId;
    options.forEach(option => {
      option.isSelected = option.serviceable && option.priceGroupId === onlyId;
    });
  }

  return {
    options,
    isMulti: options.length > 1,
    isEmpty: options.length === 0
  };
};

/**
 * Extract the raw SLA options for a given sku (productCode) from the v2
 * response. The v2 `sku` array holds single-key maps { [productCode]: [...] }.
 * When skuCode is omitted the first entry is returned (PDP typically has one
 * sku in the response).
 */
export const getSlaOptionsForSku = (
  response: RawSlaResponse | undefined,
  skuCode?: string
): RawSlaOption[] => {
  const entries: RawSkuEntry[] = response?.sku ?? [];
  if (entries.length === 0) return [];

  if (skuCode) {
    const match = entries.find(entry => entry && entry[skuCode]);
    if (match) return match[skuCode] ?? [];
  }

  const firstKey = Object.keys(entries[0] ?? {})[0];
  return (firstKey && entries[0][firstKey]) || [];
};

/** Convenience: response + sku + tags → view model, in one call. */
export const mapSlaResponse = (
  response: RawSlaResponse | undefined,
  skuCode?: string,
  tags?: PriceTypeTags
): SlaViewModel => mapSlaOptions(getSlaOptionsForSku(response, skuCode), tags);
